import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "./redux";
import { fetchNotificationsForUser } from "../store/slices/notificationSlice";
import {
  bumpUnreadForRoom,
  bumpRoomLastActivity,
  fetchChatList,
} from "../store/slices/chatSlice";
import { userIdSelector } from "../store/slices/authSlice";
import { store } from "../store/store";
import { subscribeToForegroundMessages } from "../services/firebaseMessaging";
import { SUCCESS_MESSAGES } from "../utils/constants";
import { resolvePushDataToPath } from "../utils/deepLink";
import { scheduleNewChatMessageToast } from "../helpers/chatMessageToast";
import { CHAT_PATH } from "../constants";

function getViewingChatRoomId(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  const pathname = window.location.pathname;
  if (!pathname.startsWith(`${CHAT_PATH}/`)) {
    return null;
  }
  return (
    decodeURIComponent(
      pathname.slice(CHAT_PATH.length + 1).split("/")[0] || ""
    ) || null
  );
}

/**
 * Handles in-app FCM messages. Must run inside RouterProvider (e.g. DashboardLayout).
 */
export function useForegroundPushNavigation(enabled: boolean) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userId = useAppSelector(userIdSelector);

  useEffect(() => {
    if (!enabled || !userId) {
      return;
    }

    const refreshInbox = () => {
      dispatch(fetchNotificationsForUser({ userId, silent: true }));
    };

    let active = true;
    let unsubscribe = () => {};

    subscribeToForegroundMessages((payload) => {
      const title =
        payload?.notification?.title || SUCCESS_MESSAGES.NEW_NOTIFICATION;
      const body =
        payload?.notification?.body ||
        SUCCESS_MESSAGES.YOU_HAVE_A_NEW_NOTIFICATION;
      const data = (payload?.data || {}) as Record<string, string>;
      const deepLinkPath = resolvePushDataToPath(data);

      const goToDeepLink = () => {
        if (!deepLinkPath) {
          return;
        }
        navigate(deepLinkPath);
        refreshInbox();
      };

      if (deepLinkPath && (data.chatId || data.roomId)) {
        const roomId = data.chatId || data.roomId;
        const senderName = data.senderName || title;
        if (getViewingChatRoomId() !== roomId) {
          dispatch(bumpUnreadForRoom(roomId));
          const hasRoom = store
            .getState()
            .chat.chats.some((chat) => chat.id === roomId);
          if (hasRoom) {
            dispatch(bumpRoomLastActivity(roomId));
          } else {
            void dispatch(fetchChatList({ silent: true }));
          }
        }
        scheduleNewChatMessageToast(roomId, {
          senderName,
          onClick: goToDeepLink,
        });
        goToDeepLink();
        return;
      }

      toast.info(`${title}: ${body}`, {
        position: "top-center",
        closeButton: false,
        onClick: goToDeepLink,
      });
      refreshInbox();
    })
      .then((dispose) => {
        if (!active) {
          dispose();
          return;
        }
        unsubscribe = dispose;
      })
      .catch(() => {});

    return () => {
      active = false;
      unsubscribe();
    };
  }, [enabled, userId, dispatch, navigate]);
}
